/**
 * Wire-format schemas for `<site>/wp-json/ezqr/v1/site` — the per-site
 * capability endpoint. Parses snake_case JSON exactly as the backend
 * sends it and transforms to the camelCase entity in
 * `src/entities/site-capabilities.ts`.
 *
 * Quirks handled here:
 *  - `_apiVersion` (leading underscore) → `apiVersion`
 *  - `endpoints` arrives as `[]` (empty) OR `{ k: path }` — normalised
 *    to a record before the entity sees it.
 *  - A feature key may be ABSENT for sites where the plugin isn't
 *    provisioned (passes through as undefined; consumers handle
 *    absence vs. `enabled: false` distinctly).
 *  - `site.logo` ships in TWO shapes (`string | null` historically,
 *    `{ id, url, w, h } | null` going forward). Accept both, normalise
 *    to the URL string the entity / SiteLogo component expects.
 */
import { z } from 'zod'

import {
  type AiConfig,
  type BulkPlanFeature,
  type DualPricingConfig,
  type Feature,
  type Features,
  type OnlineOrderingConfig,
  type OrderingDualPricing,
  type SiteCapabilities,
  type SiteIdentity,
} from '@/entities/site-capabilities'

/**
 * Normalise the `endpoints` field at the wire boundary: backend uses
 * `[]` (empty array) for "no endpoints" and `{ name: path }` for the
 * non-empty case. We collapse the empty-array form to `{}` so every
 * consumer can treat it as a record without a type-guard.
 */
const endpointsWire = z.preprocess(
  (v) => (Array.isArray(v) ? {} : v),
  z.record(z.string(), z.string()),
)

function featureEnvelopeWire<C extends z.ZodTypeAny, Out>(
  configWire: C,
  toEntityConfig: (raw: z.infer<C>) => Out,
) {
  return z
    .object({
      enabled: z.boolean(),
      visibility: z.string(),
      reason: z.string().nullable(),
      config: configWire,
      endpoints: endpointsWire,
    })
    .transform(
      (f): Feature<Out> => ({
        enabled: f.enabled,
        visibility: f.visibility,
        reason: f.reason,
        config: toEntityConfig(f.config),
        endpoints: f.endpoints,
      }),
    )
}

// ---- site identity wire ------------------------------------------------

/**
 * Backend has shipped `site.logo` in two shapes:
 *  - originally `string | null` (the raw URL)
 *  - now `{ id, url, w, h } | null` (an attachment object with
 *    dimensions, useful for `srcset` later)
 *
 * Accept both so the SPA survives backend upgrades / downgrades, and
 * normalise to a single URL string at the entity boundary — the
 * SiteLogo component still wants just a `string | null`. We discard
 * the dimensions for now; revisit if we ever start emitting `srcset`.
 */
const SiteLogoWire = z
  .union([z.string().url(), z.object({ url: z.string().url() }).passthrough(), z.null()])
  .transform((v): string | null => {
    if (v === null) return null
    if (typeof v === 'string') return v
    return v.url
  })

const SiteIdentityWire = z
  .object({
    id: z.number().int().positive(),
    name: z.string(),
    url: z.string().url(),
    base_url: z.string().url(),
    logo: SiteLogoWire,
    locale: z.string(),
    theme: z.object({ slug: z.string() }),
  })
  .transform(
    (s): SiteIdentity => ({
      id: s.id,
      name: s.name,
      url: s.url,
      baseUrl: s.base_url,
      logo: s.logo,
      locale: s.locale,
      theme: { slug: s.theme.slug },
    }),
  )

// ---- dual pricing config wire ------------------------------------------

const DualPricingConfigWire = z.object({
  base_price_type: z.string(),
  fee_percent: z.number(),
  fee_direction: z.string(),
})

const dualPricingToEntity = (c: z.infer<typeof DualPricingConfigWire>): DualPricingConfig => ({
  basePriceType: c.base_price_type,
  feePercent: c.fee_percent,
  feeDirection: c.fee_direction,
})

const DualPricingFeatureWire = featureEnvelopeWire(DualPricingConfigWire, dualPricingToEntity)

// ---- online ordering config wire ---------------------------------------

const OrderingDualPricingWire = z.object({
  enabled: z.boolean(),
  base_type: z.string(),
  fee_pct: z.number(),
})

const orderingDualToEntity = (c: z.infer<typeof OrderingDualPricingWire>): OrderingDualPricing => ({
  enabled: c.enabled,
  baseType: c.base_type,
  feePct: c.fee_pct,
})

const OnlineOrderingConfigWire = z.object({
  merchant_id: z.string(),
  business_name: z.string(),
  flow: z.string(),
  dualpricing: OrderingDualPricingWire,
  catalog_mode: z.boolean(),
})

const onlineOrderingToEntity = (
  c: z.infer<typeof OnlineOrderingConfigWire>,
): OnlineOrderingConfig => ({
  merchantId: c.merchant_id,
  businessName: c.business_name,
  flow: c.flow,
  dualpricing: orderingDualToEntity(c.dualpricing),
  catalogMode: c.catalog_mode,
})

const OnlineOrderingFeatureWire = featureEnvelopeWire(
  OnlineOrderingConfigWire,
  onlineOrderingToEntity,
)

// ---- AI command pipeline wire ------------------------------------------

/**
 * `ai.config` carries `{ provider, model }` plus a server-internal
 * `_private` block (caps, lock TTL) we don't model.
 *
 * Tolerant on purpose: `provider`/`model` are `.partial()` (the backend
 * docs call this block loosely-specified, and config can be `null`). This
 * matters because `/site` gates the WHOLE app — a strict `config` that
 * threw on a missing `model` would take down the dashboard, dish editor
 * and ordering view with it. We surface only the two fields we read and
 * default them to '' if absent rather than failing the manifest.
 */
const AiConfigWire = z
  .object({ provider: z.string(), model: z.string() })
  .partial()
  .passthrough()
  .nullable()

const aiConfigToEntity = (c: z.infer<typeof AiConfigWire>): AiConfig | null =>
  c ? { provider: c.provider ?? '', model: c.model ?? '' } : null

const AiFeatureWire = featureEnvelopeWire(AiConfigWire, aiConfigToEntity)

const CapabilityWire = z.object({ access: z.string(), enabled: z.boolean() })

/**
 * `bulk_plan` = base envelope (config is `null` live, kept opaque) plus a
 * `capabilities` map. `capabilities` is normalised through the same
 * empty-array→{} preprocess as endpoints (PHP serialises an empty assoc
 * array as `[]`).
 */
const BulkPlanFeatureWire = z
  .object({
    enabled: z.boolean(),
    visibility: z.string(),
    reason: z.string().nullable(),
    config: z.unknown().nullable(),
    endpoints: endpointsWire,
    capabilities: z
      .preprocess(
        (v) => (Array.isArray(v) ? {} : v),
        z.object({ images: CapabilityWire.optional() }).partial(),
      )
      .optional(),
  })
  .transform(
    (f): BulkPlanFeature => ({
      enabled: f.enabled,
      visibility: f.visibility,
      reason: f.reason,
      config: f.config ?? null,
      endpoints: f.endpoints,
      ...(f.capabilities ? { capabilities: f.capabilities } : {}),
    }),
  )

const TranscribeFeatureWire = featureEnvelopeWire(z.unknown().nullable(), (c) => c ?? null)

// Sibling AI-plan executors gated independently of `bulk_plan` (core
// 2026-07+): the plan proposes term / shared-modifier changes, but the
// review UI only surfaces each domain when its gate is enabled. Same base
// envelope + opaque config as transcribe.
const BulkTermsFeatureWire = featureEnvelopeWire(z.unknown().nullable(), (c) => c ?? null)
const BulkModifiersFeatureWire = featureEnvelopeWire(z.unknown().nullable(), (c) => c ?? null)

// ---- features bag ------------------------------------------------------

/**
 * Generic envelope for unknown future features — keeps endpoints
 * normalised and config opaque so the SPA can still surface them
 * generically (e.g. an "Unknown feature: foo (enabled)" debug row).
 */
const UnknownFeatureWire = z
  .object({
    enabled: z.boolean(),
    visibility: z.string(),
    reason: z.string().nullable(),
    config: z.unknown(),
    endpoints: endpointsWire,
  })
  .transform((f) => ({
    enabled: f.enabled,
    visibility: f.visibility,
    reason: f.reason,
    config: f.config,
    endpoints: f.endpoints,
  }))

/**
 * Make a feature slice degrade to "absent" (`undefined`) if its shape
 * drifts, instead of failing the whole manifest. `/site` gates the entire
 * app, and `.catchall` only rescues UNKNOWN keys — an explicit key that
 * throws takes the whole bag (and the site shell) down with it. Isolating
 * the blast radius means a bad `ai` block only hides the AI card; it
 * doesn't white-screen the dashboard.
 *
 * The trade-off is silence: a drifted feature vanishes with no signal,
 * which reads to the user as "the section randomly disappeared". So in dev
 * we log WHICH feature dropped and WHY (the zod issues) — turning an
 * invisible gate flip into an actionable schema-drift report. Production
 * behaviour is unchanged: degrade to `undefined`.
 */
function softFeature<S extends z.ZodTypeAny>(slug: string, schema: S) {
  return schema.optional().catch((ctx) => {
    if (import.meta.env.DEV) {
      console.warn(
        `[site-capabilities] feature "${slug}" dropped — shape drift; the section will be hidden. Issues:`,
        ctx.error.issues,
      )
    }
    return undefined
  })
}

const FeaturesWire = z
  .object({
    online_ordering: OnlineOrderingFeatureWire.optional(),
    dual_pricing: DualPricingFeatureWire.optional(),
    ai: softFeature('ai', AiFeatureWire),
    bulk_plan: softFeature('bulk_plan', BulkPlanFeatureWire),
    bulk_terms: softFeature('bulk_terms', BulkTermsFeatureWire),
    bulk_modifiers: softFeature('bulk_modifiers', BulkModifiersFeatureWire),
    transcribe: softFeature('transcribe', TranscribeFeatureWire),
  })
  .catchall(UnknownFeatureWire)
  // Each feature slice above is transformed to its concrete entity type
  // (Feature<…> / BulkPlanFeature), and the catchall yields Feature<unknown>
  // — so the bag is structurally a `Features` and TS verifies it here.
  // No `as unknown as` escape: a future entity-vs-wire drift now fails
  // the typecheck at this line instead of leaking `undefined` to the UI.
  .transform((f): Features => f)

// ---- top-level ---------------------------------------------------------

export const siteCapabilitiesWire = z
  .object({
    _apiVersion: z.literal(1),
    site: SiteIdentityWire,
    features: FeaturesWire,
  })
  .transform(
    (s): SiteCapabilities => ({
      apiVersion: s._apiVersion,
      site: s.site,
      features: s.features,
    }),
  )
